import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLine, Chapter } from "../components/Reveal";
import { SiteImg } from "../hooks/useSiteImages";
import { T, EditableTitle } from "../hooks/useSiteTexts";
import { Counter } from "../components/Counter";
import { Marquee } from "../components/Marquee";
import { IberiaMap } from "../components/IberiaMap";
import {
    IMAGES,
    SERVICES,
    PROJECT_AREAS,
    ADVANTAGES,
    ADP_TOWNS,
    EXPERIENCE_GROUPS,
} from "../data/content";

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.15]);

    return (
        <section
            ref={ref}
            className="noise relative flex min-h-screen items-end overflow-hidden"
            data-testid="hero-section"
        >
            <motion.div style={{ y }} className="absolute inset-0">
                <SiteImg
                    slot="hero"
                    fallback={IMAGES.hero}
                    alt="Frota de camiões Transportes Alzira"
                    className="h-[115%] w-full object-cover"
                    loading="eager"
                />
                <div className="absolute inset-0 bg-black/55" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-black/50" />
            </motion.div>

            <motion.div
                style={{ opacity }}
                className="relative mx-auto w-full max-w-7xl px-6 pb-16 pt-44 lg:px-10"
            >
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.05, duration: 0.8 }}
                    className="mb-8 font-mono text-xs uppercase tracking-[0.4em] text-brand"
                    data-testid="hero-overline"
                >
                    <T k="home-hero-overline" fallback="Transportes Alzira — Todo o território nacional" />
                </motion.p>
                <h1
                    className="hero-shadow font-display text-[clamp(2.9rem,8.5vw,7.5rem)] font-black uppercase leading-[0.94] tracking-tight"
                    data-testid="hero-title"
                >
                    <MaskedLine delay={0.15}>Transporte que</MaskedLine>
                    <MaskedLine delay={0.32}>
                        move <span className="text-brand">projetos.</span>
                    </MaskedLine>
                </h1>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7, duration: 0.9 }}
                    className="mt-10 flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between"
                >
                    <div className="max-w-xl">
                        <p className="text-base font-medium text-white md:text-lg" data-testid="hero-subtitle">
                            <T k="home-hero-subtitle" fallback="Experiência, capacidade e confiança no transporte rodoviário de mercadorias." />
                        </p>
                        <p className="mt-3 text-sm text-zinc-300 md:text-base">
                            <T k="home-hero-complement" fallback="Desde 1998, asseguramos soluções de transporte adaptadas às exigências de cada projeto, em todo o território nacional — e internacionais quando necessário." />
                        </p>
                    </div>
                    <div className="flex flex-col gap-4 sm:flex-row">
                        <Link
                            to="/contactos"
                            data-testid="hero-quote-button"
                            className="group inline-flex items-center justify-center gap-2 bg-brand px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-white hover:text-ink"
                        >
                            Pedir Orçamento
                            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                        <Link
                            to="/frota"
                            data-testid="hero-fleet-button"
                            className="inline-flex items-center justify-center gap-2 border border-white/30 px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-brand hover:text-brand"
                        >
                            Conhecer a nossa frota
                        </Link>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1, duration: 1 }}
                    className="mt-16 grid grid-cols-2 gap-px border border-white/15 bg-white/15 lg:grid-cols-4"
                    data-testid="hero-indicators"
                >
                    {[
                        "Desde 1998",
                        "60+ camiões · 80+ reboques",
                        "Todo o território nacional",
                        "Grande capacidade operacional",
                    ].map((item) => (
                        <div
                            key={item}
                            className="bg-ink/80 px-5 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-300 backdrop-blur-sm"
                        >
                            <span className="mr-2 inline-block h-1.5 w-1.5 bg-brand align-middle" />
                            {item}
                        </div>
                    ))}
                </motion.div>
            </motion.div>
        </section>
    );
};

const QuemSomos = () => (
    <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-40" data-testid="about-section">
        <div className="grid items-start gap-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
                <Reveal>
                    <Chapter num={1} label="Quem somos" />
                    <h2 className="mt-8 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                        <EditableTitle k="home-about-title" fallback="Experiência que se transforma em |confiança." />
                    </h2>
                </Reveal>
                <Reveal delay={0.15}>
                    <div className="mt-10 space-y-5 text-sm leading-relaxed text-zinc-400 md:text-base">
                        <p><T k="home-about-p1" fallback="Fundada em 1998, a Transportes Alzira, Lda. é uma empresa familiar especializada no transporte rodoviário de mercadorias e na prestação de serviços de transporte para obras e projetos de grande dimensão." /></p>
                        <p><T k="home-about-p2" fallback="Ao longo dos anos, construímos uma estrutura sólida, assente numa frota diversificada, numa equipa experiente e num compromisso permanente com a qualidade, segurança e cumprimento dos nossos compromissos." /></p>
                        <p><T k="home-about-p3" fallback="Trabalhamos diariamente para oferecer soluções de transporte eficientes, flexíveis e adaptadas às necessidades de cada cliente." /></p>
                    </div>
                    <Link
                        to="/empresa"
                        data-testid="about-more-button"
                        className="group mt-10 inline-flex items-center gap-3 border border-white/25 px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-brand hover:text-brand"
                    >
                        Saiba mais sobre nós
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </Reveal>
            </div>
            <div className="grid gap-6 lg:col-span-7 lg:grid-cols-12">
                <Reveal delay={0.1} className="lg:col-span-8">
                    <div className="group overflow-hidden border border-white/10">
                        <SiteImg
                            slot="about-team"
                            fallback={IMAGES.team}
                            alt="Equipa e operação logística"
                            loading="lazy"
                            className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                    </div>
                </Reveal>
                <Reveal delay={0.25} className="lg:col-span-4 lg:pt-24">
                    <div className="group overflow-hidden border border-white/10">
                        <SiteImg
                            slot="about-workshop"
                            fallback={IMAGES.workshop}
                            alt="Manutenção da frota"
                            loading="lazy"
                            className="aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                    </div>
                </Reveal>
            </div>
        </div>
    </section>
);

const Numeros = () => (
    <section className="border-y border-white/10 bg-coal" data-testid="stats-section">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px lg:grid-cols-5">
            {[
                { value: 1998, suffix: "", label: "Ano de fundação", plain: false },
                { value: 60, suffix: "+", label: "Camiões", plain: false },
                { value: 80, suffix: "+", label: "Reboques", plain: false },
                { value: 25, suffix: "+", label: "Anos de experiência", plain: false },
                { value: 0, suffix: "", label: "Área principal de atuação", plain: true, text: "PT" },
            ].map((s, i) => (
                <div
                    key={s.label}
                    className="border-white/10 px-8 py-16 lg:border-l lg:first:border-l-0"
                    data-testid={`stat-${i}`}
                >
                    <Reveal delay={i * 0.1}>
                        <div className="font-display text-5xl font-black tracking-tight text-white sm:text-6xl">
                            {s.plain ? (
                                <span className="text-brand">{s.text}</span>
                            ) : (
                                <Counter to={s.value} suffix={s.suffix} />
                            )}
                        </div>
                        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500">
                            {s.label}
                        </p>
                    </Reveal>
                </div>
            ))}
        </div>
    </section>
);

const Servicos = () => (
    <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-40" data-testid="services-section">
        <div className="mb-16 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
            <Reveal>
                <Chapter num={2} label="Serviços" />
                <h2 className="mt-8 max-w-2xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                    <EditableTitle k="home-services-title" fallback="Soluções de transporte |à medida." />
                </h2>
            </Reveal>
            <Reveal delay={0.15}>
                <Link
                    to="/servicos"
                    data-testid="services-all-link"
                    className="group inline-flex items-center gap-3 font-mono text-xs font-bold uppercase tracking-[0.25em] text-zinc-400 transition-colors duration-300 hover:text-brand"
                >
                    Ver todos os serviços
                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
            </Reveal>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
                <Reveal
                    key={s.id}
                    delay={(i % 3) * 0.12}
                    className={i === 0 ? "md:col-span-2 lg:col-span-2" : ""}
                >
                    <Link
                        to="/servicos"
                        data-testid={`service-card-${s.id}`}
                        className="group flex h-full flex-col border border-white/10 bg-coal transition-colors duration-500 hover:border-brand"
                    >
                        <div className="overflow-hidden">
                            <SiteImg
                                slot={`service-${s.id}`}
                                fallback={s.image}
                                alt={s.title}
                                loading="lazy"
                                className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                                    i === 0 ? "aspect-[2/1]" : "aspect-[16/9]"
                                }`}
                            />
                        </div>
                        <div className="flex flex-1 flex-col p-8">
                            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand">
                                {String(i + 1).padStart(2, "0")}
                            </span>
                            <h3 className="mt-3 font-display text-2xl font-black uppercase">
                                {s.title}
                            </h3>
                            <p className="mt-3 flex-1 text-sm text-zinc-400">
                                <T k={`service-short-${s.id}`} fallback={s.short} />
                            </p>
                            <span className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-white transition-colors duration-300 group-hover:text-brand">
                                Saiba mais
                                <ArrowUpRight className="h-3.5 w-3.5" />
                            </span>
                        </div>
                    </Link>
                </Reveal>
            ))}
        </div>
    </section>
);

const FrotaSection = () => (
    <section className="border-y border-white/10 bg-coal" data-testid="fleet-section">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 py-28 lg:grid-cols-12 lg:px-10 lg:py-40">
            <div className="lg:col-span-5">
                <Reveal>
                    <Chapter num={3} label="Frota" />
                    <h2 className="mt-8 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                        <EditableTitle k="home-fleet-title" fallback="Uma frota preparada para |qualquer desafio." />
                    </h2>
                    <p className="mt-8 text-sm leading-relaxed text-zinc-400 md:text-base">
                        <T k="home-fleet-text" fallback="Dispomos de uma frota diversificada de veículos pesados e equipamentos especializados, permitindo-nos responder a diferentes necessidades operacionais." />
                    </p>
                </Reveal>
                <Reveal delay={0.15}>
                    <div className="mt-10 flex flex-wrap gap-2">
                        {[
                            "Camiões basculantes",
                            "Camiões de 4 eixos",
                            "Tratores",
                            "Semirreboques basculantes",
                            "Semirreboques cisterna",
                            "Porta-máquinas",
                            "Camiões com grua",
                            "Pisos móveis",
                            "Graneleiros",
                            "Cisternas de diferentes capacidades",
                        ].map((c) => (
                            <span
                                key={c}
                                className="border border-white/15 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400"
                            >
                                {c}
                            </span>
                        ))}
                    </div>
                    <Link
                        to="/frota"
                        data-testid="fleet-all-button"
                        className="group mt-12 inline-flex items-center gap-3 bg-brand px-8 py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-white hover:text-ink"
                    >
                        Ver toda a frota
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </Link>
                </Reveal>
            </div>
            <div className="grid content-start gap-6 sm:grid-cols-2 lg:col-span-7">
                {[IMAGES.scania, IMAGES.volvo, IMAGES.dumpTruck, IMAGES.semiDriving].map(
                    (im, i) => (
                        <Reveal key={im} delay={i * 0.1} className={i % 2 === 1 ? "sm:pt-16" : ""}>
                            <div className="group overflow-hidden border border-white/10">
                                <SiteImg
                                    slot={`home-fleet-${i + 1}`}
                                    fallback={im}
                                    alt="Viatura da frota Transportes Alzira"
                                    loading="lazy"
                                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                            </div>
                        </Reveal>
                    ),
                )}
            </div>
        </div>
    </section>
);

const ProjetosSection = () => (
    <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-40" data-testid="projects-section">
        <Reveal>
            <Chapter num={4} label="Projetos" />
            <div className="mt-8 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
                <h2 className="max-w-2xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                    <EditableTitle k="home-projects-title" fallback="Experiência em operações de |grande dimensão." />
                </h2>
                <p className="max-w-md text-sm text-zinc-400 md:text-base">
                    <T k="home-projects-subtitle" fallback="Colaboramos em projetos que exigem capacidade operacional, rigor e uma resposta logística eficiente." />
                </p>
            </div>
        </Reveal>
        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {PROJECT_AREAS.slice(0, 4).map((p, i) => (
                <Reveal key={p.title} delay={i * 0.1}>
                    <Link
                        to="/projetos"
                        data-testid={`project-card-${i}`}
                        className="group relative block overflow-hidden border border-white/10"
                    >
                        <SiteImg
                            slot={`project-${i + 1}`}
                            fallback={p.image}
                            alt={p.title}
                            loading="lazy"
                            className="aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 p-6">
                            <h3 className="font-display text-xl font-black uppercase tracking-tight">
                                {p.title}
                            </h3>
                            <p className="mt-2 text-xs leading-relaxed text-zinc-400">
                                {p.desc}
                            </p>
                        </div>
                    </Link>
                </Reveal>
            ))}
        </div>
    </section>
);

const Experiencia = () => (
    <section className="border-t border-white/10" data-testid="experience-section">
        <div className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-40">
            <div className="grid gap-16 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <Reveal>
                        <Chapter num={5} label="Grandes obras" />
                        <h2 className="mt-8 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                            <EditableTitle k="home-experience-title" fallback="A nossa |experiência." />
                        </h2>
                        <p className="mt-8 max-w-lg text-sm leading-relaxed text-zinc-400 md:text-base">
                            <T k="home-experience-text" fallback="Ao longo dos anos, participámos em alguns dos mais relevantes projetos de infraestruturas em Portugal, contribuindo com a nossa experiência, capacidade operacional e uma frota preparada para responder aos desafios de cada obra." />
                        </p>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <div
                            className="mt-12 border border-brand/50 bg-coal p-8 lg:p-10"
                            data-testid="experience-adp-card"
                        >
                            <p className="font-mono text-xs font-bold uppercase tracking-[0.35em] text-brand">
                                Desde 2005
                            </p>
                            <h3 className="mt-4 font-display text-3xl font-black uppercase tracking-tight">
                                Águas de Portugal
                            </h3>
                            <div className="mt-6 flex flex-wrap gap-2">
                                {ADP_TOWNS.map((t) => (
                                    <span
                                        key={t}
                                        className="border border-white/15 px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-400"
                                    >
                                        {t}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                </div>
                <div className="lg:col-span-7">
                    {EXPERIENCE_GROUPS.map((g, gi) => (
                        <Reveal key={g.id} delay={gi * 0.1}>
                            <div
                                className={gi > 0 ? "mt-12" : ""}
                                data-testid={`experience-group-${g.id}`}
                            >
                                <h3 className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">
                                    {g.label}
                                </h3>
                                <div className="mt-5 flex flex-wrap gap-2">
                                    {g.items.map((item) => (
                                        <span
                                            key={item}
                                            className="border border-white/10 bg-coal px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.12em] text-zinc-300 transition-colors duration-300 hover:border-brand hover:text-white"
                                        >
                                            {item}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </div>
    </section>
);

const MapaSection = () => (
    <section className="border-y border-white/10 bg-coal" data-testid="map-section">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 py-28 lg:grid-cols-2 lg:px-10 lg:py-40">
            <Reveal>
                <Chapter num={6} label="Área de atuação" />
                <h2 className="mt-8 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                    <EditableTitle k="home-map-title" fallback="Operamos onde o seu projeto |precisa de nós." />
                </h2>
                <p className="mt-8 max-w-lg text-sm leading-relaxed text-zinc-400 md:text-base">
                    <T k="home-map-text" fallback="Fortemente implantados em Portugal, asseguramos serviços de transporte em todo o território nacional e realizamos operações internacionais, incluindo Espanha, sempre que o projeto o exige." />
                </p>
                <div className="mt-10 flex gap-10">
                    <div>
                        <p className="font-display text-3xl font-black text-brand">PT</p>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                            Cobertura nacional
                        </p>
                    </div>
                    <div>
                        <p className="font-display text-3xl font-black text-ember">ES</p>
                        <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                            Quando o projeto exige
                        </p>
                    </div>
                </div>
            </Reveal>
            <Reveal delay={0.2}>
                <div className="border border-white/10 bg-ink p-6 lg:p-10">
                    <IberiaMap />
                </div>
            </Reveal>
        </div>
    </section>
);

const Porque = () => (
    <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10 lg:py-40" data-testid="why-section">
        <Reveal>
            <Chapter num={7} label="Porquê Transportes Alzira" />
            <h2 className="mt-8 max-w-3xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                <EditableTitle k="home-why-title" fallback="Cinco razões. Um |compromisso." />
            </h2>
        </Reveal>
        <div className="mt-16">
            {ADVANTAGES.map((a, i) => (
                <Reveal key={a.title} delay={i * 0.06}>
                    <div
                        className="group grid items-center gap-4 border-t border-white/10 py-10 transition-colors duration-500 last:border-b hover:bg-coal sm:grid-cols-12 sm:px-6"
                        data-testid={`advantage-${i}`}
                    >
                        <span className="font-mono text-sm font-bold text-brand sm:col-span-1">
                            {String(i + 1).padStart(2, "0")}
                        </span>
                        <h3 className="font-display text-3xl font-black uppercase tracking-tight transition-transform duration-500 group-hover:translate-x-2 sm:col-span-4 sm:text-4xl">
                            {a.title}
                        </h3>
                        <p className="text-sm text-zinc-400 sm:col-span-6 md:text-base">
                            <T k={`advantage-${i}`} fallback={a.desc} />
                        </p>
                        <ArrowUpRight className="hidden h-6 w-6 text-zinc-600 transition-colors duration-300 group-hover:text-brand sm:col-span-1 sm:block" />
                    </div>
                </Reveal>
            ))}
        </div>
    </section>
);

export const CtaSection = () => (
    <section className="noise relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
            <SiteImg
                slot="cta"
                fallback={IMAGES.nightHighway}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-ink/85" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 py-32 lg:px-10 lg:py-44">
            <Reveal>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-brand">
                    Vamos trabalhar juntos
                </p>
                <h2 className="mt-6 max-w-4xl font-display text-[clamp(2.4rem,6vw,5.5rem)] font-black uppercase leading-[0.98] tracking-tight">
                    <EditableTitle k="home-cta-title" fallback="Tem um projeto para |transportar?" />
                </h2>
                <p className="mt-8 max-w-xl text-base text-zinc-300 md:text-lg">
                    <T k="home-cta-subtitle" fallback="Fale connosco e encontre a solução certa para a sua operação." />
                </p>
                <div className="mt-12 flex flex-col gap-4 sm:flex-row">
                    <Link
                        to="/contactos"
                        data-testid="cta-quote-button"
                        className="group inline-flex items-center justify-center gap-2 bg-brand px-10 py-5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-white hover:text-ink"
                    >
                        Pedir Orçamento
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                    <Link
                        to="/contactos"
                        data-testid="cta-contact-button"
                        className="inline-flex items-center justify-center gap-2 border border-white/30 px-10 py-5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-brand hover:text-brand"
                    >
                        Contactar
                    </Link>
                </div>
            </Reveal>
        </div>
    </section>
);

export default function Home() {
    return (
        <>
            <Hero />
            <Marquee />
            <QuemSomos />
            <Numeros />
            <Servicos />
            <FrotaSection />
            <ProjetosSection />
            <Experiencia />
            <MapaSection />
            <Porque />
            <CtaSection />
        </>
    );
}
